import React, { type FC, useCallback, useEffect, useMemo } from "react";
import { Form, Grid, Loader, Segment } from "semantic-ui-react";

import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as protocolApi from "../../../../apis/protocolApi";
import * as areaUnderTheCurveJs from "../../../Lumber/AreaUnderTheCurve";
import ProtocolAnalysisTopBar from "../../../Lumber/ProtocolAnalysisTopBar";
import LineChart from "../../Widgets/Charts/lineChart";
import AnovaTable from "./AnovaTable";
import ProtocolBoxWhiskerChart from "./ProtocolBoxWhiskerChart";
import ProtocolDataNormalizationTable from "./ProtocolDataNormalizationTable";
import ProtocolSiteSpecificTable from "./ProtocolSiteSpecificTable";
import ProtocolTimecourseSiteSpecificTable from "./ProtocolTimecourseSiteSpecificTable";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import _ from "lodash";
import { std } from "mathjs";
import moment from "moment";
import { toast } from "react-toastify";
import { getProtocolAnalysisResultsForTimecourse } from "../../../../apis/analysisApi";
import MetaTagsTable from "./MetaTagsTable";
import { useProtocolAnalysis, useProtocolAnalysisDispatch } from "./ProtocolAnalysisContext";
import { useProtocolDataQuery } from "./queries";
import {
	buildMeanComparisonLetters,
	buildTreatmentMatrix,
	CUSTOM_TRIAL_FILTER,
	type DropdownOption,
	getComparableTimeSeriesPresets,
	getDataNormalizationOptions,
	getTimecourseAnalysisOptionRows,
	getUncheckedTreatmentIds,
	getUncheckedTrialIds,
	isUniformityAnalysis,
} from "./utils";
import { getProtocolTukeyDataAsync } from "../../../../apis/statisticsApi";

import type { ProtocolPlotAnalysisResult } from "../../../../apis/types/analysisTypes";
import type {
	ProtocolMeanComparisonRequestRow,
	ProtocolMeanComparisonRow,
} from "../../../../apis/types/statisticsTypes";
import type { GduByDay } from "../../../../apis/types/weatherDataTypes";
import type {
	CompiledMeanComparisonRequest,
	CompiledMeanComparisonResult,
	FlattenedTrialMetadata,
	ProtocolTimecourseProps,
	TimecourseAnovaData,
	TimecourseAucLetteringPair,
	TimecourseAucMeanComparison,
	TimecourseAucRow,
	TimecourseCompiledRow,
	TimecoursePlotRow,
	TimecourseSitePlot,
	TimecourseSiteRow,
} from "./types";
import sharedStyles from "./ProtocolAnalysis.module.css";

/* -------- typed views of untyped JS modules ----------------------------------------------- */

/** A flight row as AreaUnderTheCurve.addEmptyFlightForPlantOrHarvestDate synthesizes it. */
type SynthesizedFlightRow = {
	analysisId: string;
	analysisName: string;
	clientId: string | null;
	flightDate: string;
	flightId: string;
	flightName: string;
	plotAnalysisResultValue: number;
	plotReplicate: number | null;
	plotRange: number;
	trialAnalysisResultAverageValue: number;
	trialAnalysisResultValues: null;
	/** A random UUID, not the trial's id. */
	trialId: string;
	trialName: string;
	trialSampleSize: string;
	trialTreatmentId: number;
	excluded: boolean;
	excludeFromAssessment: boolean;
	isHarvestFlight: boolean;
	isPlantFlight: boolean;
	quantifiedRegionTypeId: null;
	analysisTypeId: null;
	isOutlier3: boolean;
	isOutlier1_5: boolean;
	/** Absent: synthesized rows have no field. */
	fieldId?: undefined;
};

/** A flight row as the AreaUnderTheCurve helpers take it: fetched, or synthesized for plant/harvest. */
type AucInputRow = ProtocolPlotAnalysisResult | SynthesizedFlightRow;

/** AreaUnderTheCurve.calculateCumulativeGdu's result. `gdu` is a `toFixed(0)` string. */
type CumulativeGdu = { flightId: string | null; gdu: string };

/** One row of AreaUnderTheCurve.setupTrialAverages' result, as LineChart receives it. */
type TrialAverageRow = {
	analysisId: string | null;
	analysisName: string | null;
	/**
	 * Copied from the treatment's first row. Undefined when that is a fetched row (the protocol view
	 * model has no clientId). Never read.
	 */
	clientId?: string | null;
	flightDate: string | null;
	flightId: string | null;
	flightName: string | null;
	treatmentName: string | null;
	trialId: string;
	trialName: string | null;
	trialTreatmentId: number | null;
	trialAnalysisResultAverageValue: number;
	isHarvestFlight: boolean;
	isPlantFlight: boolean;
	uncheckFlight: boolean | null;
	analysisTypeId: string | null;
	unbalanced?: boolean;
};

/** The AreaUnderTheCurve functions this page calls, typed (the module is untyped JS). */
type AreaUnderTheCurveModule = {
	getAreaUnderTheCurve: (
		data: AucInputRow[],
		analysisValue: string | null,
		trialDataResults: FlattenedTrialMetadata,
		useGDU: boolean,
		cumulativeGdus: null,
		removeDamagedOrExcluded: boolean,
		removeOutliers: boolean,
		outlierType: number,
		gduByDay?: { trialId: string; data: GduByDay[] }[] | null,
		/**
		 * TODO(types): getAreaUnderTheCurve takes 9 parameters; this page passes a 10th (the committed
		 * unchecked treatment ids), which is ignored. Declared only so the call keeps passing it.
		 */
		ignoredUncheckedTreatmentIds?: number[],
	) => TimecourseAucRow[];
	addEmptyFlightForPlantOrHarvestDate: (
		date: string | null,
		treatments: AucInputRow[],
		trialTreatments: FlattenedTrialMetadata["treatments"],
		analyses: DropdownOption[] | null,
		isHarvestFlight: boolean,
		isPlantFlight: boolean,
		currentClientId: string | null,
	) => SynthesizedFlightRow[] | undefined;
	setupTrialAverages: (
		res: ProtocolPlotAnalysisResult[],
		analyses: string | null,
		analysisIndex: number,
		trialDataResults: FlattenedTrialMetadata,
		addPlantFight: boolean,
		addHarvestFlight: boolean,
		removeDamagedOrExcluded: boolean,
		removeOutliers: boolean,
		outlierType: number,
	) => TrialAverageRow[];
	calculateCumulativeGdu: (data: AucInputRow[] | null, gduByDay: GduByDay[]) => CumulativeGdu[] | undefined;
	checkForGDUFormula: (cropName: string | null | undefined) => boolean;
};

// The one cast: the module is untyped JS.
const areaUnderTheCurve = areaUnderTheCurveJs as unknown as AreaUnderTheCurveModule;

/**
 * An AUC row as this page reads it. getAreaUnderTheCurve never sets these fields, so every read
 * of them is undefined.
 */
type AucRowAsRead = Omit<TimecourseAucRow, "excluded"> & {
	/** Hard-coded false by getAreaUnderTheCurve; widened so the page's `!== true` checks type-check. */
	excluded: boolean;
	excludeFromAssessment?: undefined;
	isOutlier3?: undefined;
	isOutlier1_5?: undefined;
	flightDate?: undefined;
	plotAnalysisResultValue?: undefined;
};

/**
 * A `_.filter` result of site rows, as the timecourse compiled rows read it: as if it were one
 * row. Every such read is undefined (see the TODO(types) in `compiledData`).
 */
type SiteRowsReadAsRow = TimecourseSiteRow[] &
	Pick<TimecourseCompiledRow, "trialId" | "trialName" | "city" | "region" | "country" | "countryCode">;

// HELPER FUNCTIONS
function buildAnovaData(
	meanComparisonResult: ProtocolMeanComparisonRow | undefined,
	analysisTypeId: string | null | undefined,
): TimecourseAnovaData | null {
	if (!meanComparisonResult) return null;

	return {
		numeratorDf: meanComparisonResult.numeratorDF,
		denominatorDf: meanComparisonResult.denominatorDF,
		fValues: meanComparisonResult.fValues,
		pValues: meanComparisonResult.pValues,
		analysisId: meanComparisonResult.assessmentId,
		analysisTypeId: analysisTypeId,
	};
}

const ProtocolTimecourse: FC<ProtocolTimecourseProps> = ({
	dashboardOptions,
	standCountAnalyses,
	treatmentData,
	trialMetadataResponse,
	metadataLoading,
	trialAnalysisOptions,
	gduByDay,
	toggleMetaFactorModal,
}) => {
	/* -------- wiring ---------------------------------------------------------------------- */

	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const moduleNavigation = useModuleNavigation();

	const { pending, committed, display, isLettersOutdated, isAucOutdated: hideLineCharts } = useProtocolAnalysis();
	const { changeSettings, changeDisplaySettings, initializeSettings, commitSettings } = useProtocolAnalysisDispatch();

	const trialIds = dashboardOptions.trialIds;

	// Picking an assessment applies straight away, so the ids it resolves are read from `pending`.
	const selectedOrthoTypeOption = pending.analysisId;
	const selectedAnalysisTypeOption = pending.analysisTypeId;
	const selectedQuantifiedRegionTypeOption = pending.quantifiedRegionTypeId;
	// Everything else waits for Update Analytics, so the pipeline reads it from `committed`.
	const selectedNormalizationOption = committed.dataNormalizationId;
	const selectedMeanComparisonTestOption = committed.meanComparisonOptionId;
	const selectedAlphaOption = committed.alphaOptionId;
	const useGdu = committed.useGdu;
	const timecourseStart = committed.timecourseStart;
	const timecourseEnd = committed.timecourseEnd;
	const removeDamagedOrExcluded = committed.removeDamagedOrExcluded;
	const removeOutliers = committed.removeOutliers;
	const outlierType = committed.outlierType;
	// Same value as timecourseStart; the line charts name it separately.
	const initialTimecourseStart = committed.timecourseStart;
	// Display-only state.
	const selectedAssessmentName = display.assessmentName;
	const disableColors = display.disableColors;
	const showAveragesChecked = display.showAverages;
	const showTwoWayAnova = display.anovaType === "two-way";

	// Statistics follow the committed selection; ticking a checkbox only stages the change.
	// The charts follow `pending` instead.
	const committedUncheckedTrialIds = useMemo(
		() => getUncheckedTrialIds(trialIds, committed.selectedTrialIds),
		[trialIds, committed.selectedTrialIds],
	);

	const committedUncheckedTreatmentIds = useMemo(
		() => getUncheckedTreatmentIds(committed.selectedTreatmentMatrix),
		[committed.selectedTreatmentMatrix],
	);

	// The live checkbox state, read off `pending`. Everything displayed is stamped from these two.
	const isTickedTreatment = useCallback(
		(trialTreatmentId: number) =>
			pending.selectedTreatmentMatrix ? !!pending.selectedTreatmentMatrix[trialTreatmentId] : true,
		[pending.selectedTreatmentMatrix],
	);

	const isTickedTrial = useCallback(
		(trialId: string) => (pending.selectedTrialIds ? pending.selectedTrialIds.includes(trialId) : true),
		[pending.selectedTrialIds],
	);

	// Selection shapes the tables expect. Declared above the queries: a query key is built during
	// render, so declaring these below them hits the temporal dead zone.
	const treatmentsChecked = useMemo(() => {
		if (!treatmentData) return null;
		return _.map(treatmentData, (td) => ({
			trialTreatmentId: td.trialTreatmentId,
			treatmentChecked: isTickedTreatment(td.trialTreatmentId),
		}));
	}, [treatmentData, isTickedTreatment]);

	const trialsChecked = useMemo(() => {
		if (!trialIds) return null;
		return _.map(trialIds, (t) => ({
			trialId: t,
			trialChecked: isTickedTrial(t),
		}));
	}, [trialIds, isTickedTrial]);

	//TODO: This is here till we decide to move normalization to backend or not
	const dataNormalizationOptions = useMemo(
		() => getDataNormalizationOptions(dashboardOptions.dataNormalizations, treatmentData),
		[dashboardOptions, treatmentData],
	);

	/* -------- server data ----------------------------------------------------------------- */

	// Dropdown options come from the dashboard options, so an assessment can be picked before any
	// timecourse data is fetched.
	const analysisOptionRows = useMemo(
		() => getTimecourseAnalysisOptionRows(dashboardOptions.trialAnalysisOptions),
		[dashboardOptions],
	);

	const timeSeriesPresets = useMemo(
		() => getComparableTimeSeriesPresets(dashboardOptions.timeSeriesPresets ?? [], analysisOptionRows),
		[dashboardOptions, analysisOptionRows],
	);

	// The server filters on the uniformity flag, not on analysisTypeId. Derived from the same
	// analysisTypeId the page filters on below. With no analysis type picked, both kinds of row come back.
	const selectedIsPlotUniformity = selectedAnalysisTypeOption
		? isUniformityAnalysis(dashboardOptions.trialAnalysisOptions, selectedAnalysisTypeOption)
		: null;

	const { data: { rawTrialDataResponse } = {} } = useQuery({
		queryKey: [
			"protocol timecourse data",
			moduleNavigation.protocolId,
			userAuth.currentClientId,
			selectedOrthoTypeOption,
			selectedQuantifiedRegionTypeOption,
			selectedIsPlotUniformity,
			removeDamagedOrExcluded,
		],
		queryFn: async () =>
			getProtocolAnalysisResultsForTimecourse(
				// `enabled` gates the query on both ids
				userAuth.currentClientId!,
				moduleNavigation.protocolId!,
				selectedOrthoTypeOption,
				selectedQuantifiedRegionTypeOption,
				selectedIsPlotUniformity,
				removeDamagedOrExcluded,
				await getTokenSilently(),
			),
		placeholderData: keepPreviousData,
		select: (data) => {
			//Filter out excluded plots and outliers
			// TODO(types): the backend always sends isOutlier3/isOutlier1_5 as false (never undefined), so
			// this is false and the outlier filter below runs, but on flags that are never set: removing
			// outliers is a no-op.
			const skipOutlierFilter = !_.some(
				data.plotAnalysisResults,
				(rd) => rd?.isOutlier3 !== undefined || rd?.isOutlier1_5 !== undefined,
			);

			//-- REMOVING STAND COUNT DATA FROM TIMECOURSE PAGE
			const filteredData = _.filter(data.plotAnalysisResults, (r) => {
				return (
					(removeDamagedOrExcluded ? r.excludeFromAssessment === false : true) &&
					(skipOutlierFilter ||
						(removeOutliers && outlierType === 3
							? r.isOutlier3 === false
							: removeOutliers && outlierType === 1.5
								? r.isOutlier1_5 === false
								: true)) &&
					!r.analysisName?.includes("Stand Count")
				);
			});

			return {
				rawTrialDataResponse: _.values(_.groupBy(filteredData, (fd) => fd.trialId)),
			};
		},
		// Without an analysis the endpoint would return every analysis in the protocol
		enabled: !!moduleNavigation.protocolId && !!userAuth.currentClientId && !!selectedOrthoTypeOption,
	});

	const { data: singleTrialAnovaValues, isFetching: singleTrialTukeyRunning } = useQuery({
		queryKey: [
			"auc mean comparison letters protocol",
			moduleNavigation.protocolId,
			userAuth.currentClientId,
			pending.analysisId,
			pending.analysisTypeId,
			pending.quantifiedRegionTypeId,
			committed.meanComparisonOptionId,
			committed.alphaOptionId,
			committed.dataNormalizationId,
			committed.useGdu,
			committed.removeDamagedOrExcluded,
			committed.selectedTrialIds,
			committed.selectedTreatmentMatrix,
			timecourseStart,
			timecourseEnd,
		],
		queryFn: async () => {
			const accessToken = await getTokenSilently();

			const useTimecourseRange = true;

			const isUniformity =
				// matchesProperty rather than a `{ analysisTypeId }` matcher, which cannot be typed with a nullable id
				_.find(trialAnalysisOptions, ["analysisTypeId", pending.analysisTypeId])?.analysisTypeName === "Uniformity";

			return protocolApi.getAucMeanComparisonLettersForProtocolAsync(
				committedUncheckedTrialIds,
				committedUncheckedTreatmentIds,
				moduleNavigation.protocolId!,
				userAuth.currentClientId!,
				pending.analysisId,
				pending.analysisTypeId,
				null,
				null,
				null,
				pending.quantifiedRegionTypeId,
				committed.meanComparisonOptionId!,
				committed.alphaOptionId!,
				// TODO(types): the query is not gated on a normalization, so this can be sent as null.
				committed.dataNormalizationId!,
				committed.useGdu,
				committed.removeDamagedOrExcluded,
				isUniformity,
				useTimecourseRange ? timecourseStart : 0,
				useTimecourseRange ? timecourseEnd : 0,
				accessToken,
			);
		},
		select: (data): TimecourseAucMeanComparison[] => {
			const aucs = _.map(data.data, (r) => {
				const pairs = _.map(r.letteringPairs, (lp) => {
					const pair: TimecourseAucLetteringPair = {
						...lp,
						trialId: r.trialId,
						trialTreatmentId: lp.treatmentId,
						tukeyLetters: lp.letter,
						pValue: r.pValue,
						cv: r.cv,
						mean: r.mean,
					};
					return pair;
				});

				const auc: TimecourseAucMeanComparison = { ...r, letteringPairs: pairs };

				return auc;
			});

			return aucs;
		},
		enabled:
			!!committed.meanComparisonOptionId &&
			!!committed.alphaOptionId &&
			!!committed.alphaOptionId &&
			!!moduleNavigation.protocolId &&
			!!userAuth.currentClientId,
	});

	/* -------- local state ----------------------------------------------------------------- */

	//-- Data Sources
	//index.tsx only mounts this page once the protocol has loaded, so this reads the cached copy
	const { data: protocolDataResponse } = useProtocolDataQuery({ refetchOnMount: false });
	//0 until the protocol data is available
	const treatmentCount = protocolDataResponse?.protocolData?.treatments ?? 0;
	const trialData = trialMetadataResponse;
	const loading = metadataLoading;

	/* -------- derived values -------------------------------------------------------------- */

	// True while the user has staged a trial change that the displayed statistics do not reflect yet.
	const hideUncheckedTrialData = _.xor(committed.selectedTrialIds, pending.selectedTrialIds).length > 0;

	const initialGroupTrialDataCheckedCount = useMemo(() => {
		if (!treatmentData) return [];
		const matrix = committed.selectedTreatmentMatrix;
		return _.map(_.orderBy(treatmentData, "trialTreatmentId"), (t) => (matrix ? !!matrix[t.trialTreatmentId] : true));
	}, [treatmentData, committed.selectedTreatmentMatrix]);

	/* -------- data normalization ---------------------------------------------------------- */

	//TODO: Should move this into a seperate js file

	function getRankOrder<TRow extends { average: number; trialTreatmentId: number }>(averageData: TRow[]) {
		averageData = _.orderBy(averageData, ["average"], ["desc"]);
		_.map(averageData, (ad, index) => {
			ad.average = index + 1;
		});

		return _.sortBy(averageData, "trialTreatmentId");
	}

	function getPercentMean(trialData: AucRowAsRead[]) {
		const data = _.map(
			_.filter(trialData, (td) => {
				// TODO(types): AUC rows have no plotAnalysisResultValue, so this half is always true.
				return td.excluded !== true && td.plotAnalysisResultValue !== null;
			}),
			"totalAuc",
		);
		const mean = _.meanBy(data);
		return mean;
	}

	const getPercentByTreatmentType = useCallback(
		(trialData: AucRowAsRead[]) => {
			// `treatmentTypeId` is camel case, as getDataNormalizationOptions() builds it. The lowercase
			// spelling matches no treatment, so the mean is NaN and the isNaN filter drops every row.
			const treatmentTypeId = _.find(dataNormalizationOptions, (dno) => {
				return dno.value === selectedNormalizationOption;
			})?.treatmentTypeId;
			const trialTreament = _.find(treatmentData, (td) => {
				return td.treatmentTypeId === treatmentTypeId;
			});
			const data = _.map(
				_.filter(trialData, (td) => {
					return (
						td.trialTreatmentId === trialTreament?.trialTreatmentId && td.excluded !== true && td.totalAuc !== null
					);
				}),
				"totalAuc",
			);
			const mean = _.meanBy(data);
			return mean;
		},
		[dataNormalizationOptions, selectedNormalizationOption, treatmentData],
	);

	const getDataNormalizationMultiplier = useCallback(() => {
		// TODO(types): throws when the committed normalization is not among the options (this and
		// every other unguarded `_.find(dataNormalizationOptions, ...)!.text` in this file).
		const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
			"value",
			selectedNormalizationOption,
		])!.text.toLowerCase();

		switch (selectedDataNormalizationName) {
			case "% mean":
			case "% control":
			case "% commercial":
			case "% gsp":
				return 100;
			case "none":
			case "rank order":
			default:
				//No modifications to none, just display raw data
				return 1;
		}
	}, [dataNormalizationOptions, selectedNormalizationOption]);

	const getDataNormalizationModifier = useCallback(
		(trialData: AucRowAsRead[]) => {
			const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
				"value",
				selectedNormalizationOption,
			])!.text.toLowerCase();
			switch (selectedDataNormalizationName) {
				case "% mean":
					return getPercentMean(trialData);
				case "% control":
				case "% commercial":
				case "% gsp":
					return getPercentByTreatmentType(trialData);
				case "none":
				case "rank order":
				default:
					//No modifications to none, just display raw data
					return 1;
			}
		},
		[dataNormalizationOptions, getPercentByTreatmentType, selectedNormalizationOption],
	);

	/* -------- pipeline: raw protocol data -> timecourse data ------------------------------ */

	const rawTrialData = useMemo(() => {
		if (!rawTrialDataResponse) return rawTrialDataResponse;
		return _.map(rawTrialDataResponse, (r) => {
			// Number() makes the null -> 0 coercion of trialTreatmentId explicit
			return _.filter(r, (trial) => Number(trial.trialTreatmentId) <= treatmentCount);
		});
	}, [rawTrialDataResponse, treatmentCount]);

	const resetTimecourseRange = useCallback(() => {
		toast.error("Timecourse range could not find enough flights in trials", {
			autoClose: 10000,
			toastId: "timecourseRangeError",
		});

		changeSettings({ timecourseStart: 0, timecourseEnd: 0 });
	}, [changeSettings]);

	const timecourseData = useMemo(() => {
		if (!rawTrialData) return null;

		const flattenedRawTrialData = _.flatten(rawTrialData);
		// No readiness guard: gating on compiledData would be a cycle, since compiledData reads timecourseData.
		if (timecourseStart !== null && timecourseEnd !== null) {
			let dapTrialData: ProtocolPlotAnalysisResult[] = [];
			if (timecourseEnd === 0) {
				return flattenedRawTrialData;
			} else {
				if (!useGdu) {
					_.map(trialData, (td) => {
						const firstFlightDate =
							td.trialInfo.plantDate ??
							_.minBy(
								_.filter(flattenedRawTrialData, (x) => {
									return x.trialId === td.trialInfo.trialId;
								}),
								"flightDate",
							)?.flightDate;
						const flightDateFilteredResults = _.filter(flattenedRawTrialData, (adr) => {
							//TODO: Swap out for luxon
							return (
								adr.trialId === td.trialInfo.trialId &&
								moment(adr.flightDate) <= moment(firstFlightDate).add(Number(timecourseEnd), "d") &&
								moment(adr.flightDate) >= moment(firstFlightDate).add(Number(timecourseStart), "d")
							);
						});

						//Check if enough flights exist in timecourse range
						const singleFlightInRange = _.uniqBy(flightDateFilteredResults, (f) => f.flightDate)?.length < 2;

						if (singleFlightInRange) {
							dapTrialData = [];
							resetTimecourseRange();
							return;
						}

						dapTrialData = dapTrialData.concat(flightDateFilteredResults);
					});
				}

				if (useGdu) {
					_.map(trialData, (td) => {
						const trialsGDUsByDay = _.find(gduByDay, (gdu) => {
							return gdu.trialId === td.trialInfo.trialId;
						});

						let flightGDUs = areaUnderTheCurve.calculateCumulativeGdu(
							flattenedRawTrialData,
							// TODO(types): throws when gduByDay is null or has no entry for the trial.
							trialsGDUsByDay!.data,
						);

						flightGDUs = _.filter(flightGDUs, (fg) => {
							return Number(fg.gdu) <= timecourseEnd && Number(fg.gdu) >= timecourseStart;
						});

						const flightDateFilteredResults = _.filter(flattenedRawTrialData, (adr) => {
							return (
								adr.trialId === td.trialInfo.trialId &&
								_.some(flightGDUs, (fg) => {
									return fg.flightId === adr.flightId;
								})
							);
						});

						//Check if enough flights exist in timecourse range
						const singleFlightInRange = _.uniqBy(flightDateFilteredResults, (f) => f.flightDate)?.length < 2;

						if (singleFlightInRange) {
							dapTrialData = [];
							resetTimecourseRange();
							return;
						}

						dapTrialData = dapTrialData.concat(flightDateFilteredResults);
					});
				}
			}

			return dapTrialData.length > 0 ? dapTrialData : flattenedRawTrialData;
		} else {
			return flattenedRawTrialData;
		}
	}, [gduByDay, rawTrialData, resetTimecourseRange, timecourseEnd, timecourseStart, trialData, useGdu]);

	const orthoTypeOptions = useMemo(() => {
		if (!timecourseData) return null;
		return _.uniqBy(
			// The type guard only narrows; the check is plain truthiness
			_.filter(
				timecourseData,
				(tcd): tcd is ProtocolPlotAnalysisResult & { analysisId: string; analysisName: string } =>
					!!(tcd.analysisId && tcd.analysisName),
			),
			"analysisId",
		).map(function (analysis): DropdownOption {
			return { key: analysis.analysisId, value: analysis.analysisId, text: analysis.analysisName };
		});
	}, [timecourseData]);

	const filterTimeCourseDataByStartAndEndTimes = useCallback(
		(updatedRawTrialData: ProtocolPlotAnalysisResult[][] | undefined) => {
			let dapTrialData: ProtocolPlotAnalysisResult[] = [];
			const flattenedRawTrialData = _.flatten(updatedRawTrialData);
			if (timecourseEnd == 0) {
				return flattenedRawTrialData;
			}

			if (!useGdu) {
				_.map(trialData, (td) => {
					const firstFlightDate =
						td.trialInfo.plantDate ??
						_.minBy(
							_.filter(flattenedRawTrialData, (x) => {
								return x.trialId === td.trialInfo.trialId;
							}),
							"flightDate",
						)?.flightDate;
					const flightDateFilteredResults = _.filter(flattenedRawTrialData, (adr) => {
						return (
							adr.trialId === td.trialInfo.trialId &&
							moment(adr.flightDate) <= moment(firstFlightDate).add(Number(timecourseEnd), "d") &&
							moment(adr.flightDate) >= moment(firstFlightDate).add(Number(timecourseStart), "d")
						);
					});

					//-- Check if enough flights exist in timecourse range
					const singleFlightInRange = _.uniqBy(flightDateFilteredResults, (f) => f?.flightDate)?.length < 2;

					if (singleFlightInRange) {
						dapTrialData = flattenedRawTrialData;

						resetTimecourseRange();
						return;
					}

					dapTrialData = dapTrialData.concat(flightDateFilteredResults);
				});
			}

			if (useGdu) {
				_.map(trialData, (td) => {
					const trialsGDUsByDay = _.find(gduByDay, (gdu) => {
						return gdu.trialId === td.trialInfo.trialId;
					});

					let flightGDUs = areaUnderTheCurve.calculateCumulativeGdu(
						flattenedRawTrialData,
						// TODO(types): throws when gduByDay is null or has no entry for the trial.
						trialsGDUsByDay!.data,
					);

					flightGDUs = _.filter(flightGDUs, (fg) => {
						return Number(fg.gdu) <= timecourseEnd && Number(fg.gdu) >= timecourseStart;
					});

					const flightDateFilteredResults = _.filter(flattenedRawTrialData, (adr) => {
						return (
							adr.trialId === td.trialInfo.trialId &&
							_.some(flightGDUs, (fg) => {
								return fg.flightId === adr.flightId;
							})
						);
					});

					//-- Check if enough flights exist in timecourse range
					const singleFlightInRange = _.uniqBy(flightDateFilteredResults, (f) => f.flightDate)?.length < 2;

					if (singleFlightInRange) {
						dapTrialData = flattenedRawTrialData;

						resetTimecourseRange();
						return;
					}

					dapTrialData = dapTrialData.concat(flightDateFilteredResults);
				});
			}
			return dapTrialData;
		},
		[gduByDay, resetTimecourseRange, timecourseEnd, timecourseStart, trialData, useGdu],
	);

	const filteredTrialData = useMemo(() => {
		const tempTimecourseData = filterTimeCourseDataByStartAndEndTimes(rawTrialData);

		const timeCourseDataCopy = _.cloneDeep(tempTimecourseData);
		const timeCourseDataTemp = _.filter(timeCourseDataCopy, (tcd) => {
			return tcd.analysisId == selectedOrthoTypeOption && tcd.analysisTypeId === selectedAnalysisTypeOption;
		});

		const trialDataCopy = _.cloneDeep(trialData);
		const trialDataTemp = _.filter(trialDataCopy, (tdc) => {
			return _.some(timeCourseDataTemp, (tcdt) => {
				return tcdt.trialId == tdc.trialInfo.trialId;
			});
		});

		return trialDataTemp;
	}, [
		filterTimeCourseDataByStartAndEndTimes,
		rawTrialData,
		selectedAnalysisTypeOption,
		selectedOrthoTypeOption,
		trialData,
	]);

	/* -------- pipeline: area under the curve ---------------------------------------------- */

	const filteredAucData = useMemo(() => {
		// AucRowAsRead only adds fields the AUC rows never have, so this widening is safe
		const auc: AucRowAsRead[][] = [];
		const uncheckedList = committedUncheckedTreatmentIds;
		_.map(filteredTrialData, (trialsD) => {
			let plantAndHarvestFlightValues: SynthesizedFlightRow[] = [];

			if (timecourseStart !== null && Number(timecourseStart) === 0) {
				if (trialsD.trialInfo.harvestDate) {
					const harvestTreatments = areaUnderTheCurve.addEmptyFlightForPlantOrHarvestDate(
						trialsD.trialInfo.harvestDate,
						_.filter(timecourseData, (tcd) => {
							return (
								tcd.trialId == trialsD.trialInfo.trialId &&
								tcd.quantifiedRegionTypeId === selectedQuantifiedRegionTypeOption
							);
						}),
						trialsD.treatments,
						orthoTypeOptions,
						true,
						false,
						userAuth.currentClientId,
					);
					if (harvestTreatments) {
						plantAndHarvestFlightValues = plantAndHarvestFlightValues.concat(harvestTreatments);
					}
				}

				if (trialsD.trialInfo.plantDate) {
					const plantTreatments = areaUnderTheCurve.addEmptyFlightForPlantOrHarvestDate(
						trialsD.trialInfo.plantDate,
						_.filter(timecourseData, (tcd) => {
							return (
								tcd.trialId == trialsD.trialInfo.trialId &&
								tcd.quantifiedRegionTypeId === selectedQuantifiedRegionTypeOption
							);
						}),
						trialsD.treatments,
						orthoTypeOptions,
						false,
						true,
						userAuth.currentClientId,
					);

					if (plantTreatments) {
						plantAndHarvestFlightValues = plantAndHarvestFlightValues.concat(plantTreatments);
					}
				}
			}

			auc.push(
				areaUnderTheCurve.getAreaUnderTheCurve(
					[
						..._.filter(timecourseData, (tcd) => {
							return (
								tcd.trialId == trialsD.trialInfo.trialId &&
								tcd.quantifiedRegionTypeId === selectedQuantifiedRegionTypeOption &&
								tcd.analysisTypeId === selectedAnalysisTypeOption
							);
						}),
						...plantAndHarvestFlightValues,
					],
					selectedOrthoTypeOption,
					trialsD,
					useGdu,
					null,
					removeDamagedOrExcluded,
					removeOutliers,
					outlierType,
					_.filter(gduByDay, (gbd) => {
						return gbd.trialId == trialsD.trialInfo.trialId;
					}),
					uncheckedList,
				),
			);
		});
		return auc;
	}, [
		committedUncheckedTreatmentIds,
		filteredTrialData,
		gduByDay,
		orthoTypeOptions,
		outlierType,
		removeDamagedOrExcluded,
		removeOutliers,
		selectedAnalysisTypeOption,
		selectedOrthoTypeOption,
		selectedQuantifiedRegionTypeOption,
		timecourseData,
		timecourseStart,
		useGdu,
		userAuth.currentClientId,
	]);

	/* -------- pipeline: site specific data ------------------------------------------------ */

	const { filteredSiteSpecificData, trialPlotSpecificData } = useMemo(() => {
		let siteData: TimecourseSiteRow[] = [];
		const plotObjects: TimecoursePlotRow[] = [];
		const orthoTypeName = _.find(orthoTypeOptions, (oto) => {
			return oto.value === selectedOrthoTypeOption;
		})?.text;

		const uncheckedList = committedUncheckedTreatmentIds;
		//need to get rid of the duplicates
		_.map(filteredAucData, (aucTrialD) => {
			const filterAucTrialD = _.filter(aucTrialD, (atd) => {
				return !_.includes(uncheckedList, atd.trialTreatmentId);
			});
			const dataNormalization = getDataNormalizationModifier(filterAucTrialD);

			const trialData: TimecourseSiteRow[] = [];

			for (let i = 1; i <= treatmentCount; ++i) {
				// The type guards only narrow trialTreatmentId
				const aucTreatmentData = _.map(
					_.filter(aucTrialD, (trial): trial is AucRowAsRead & { trialTreatmentId: number } => {
						return trial.trialTreatmentId === i;
					}),
				);

				if (aucTreatmentData?.length === 0) continue;

				const trialTreatmentPlotData = _.map(
					_.filter(aucTrialD, (trial): trial is AucRowAsRead & { trialTreatmentId: number } => {
						return trial.trialTreatmentId === i && trial.excluded !== true;
					}),
					(d): TimecourseSitePlot => {
						return {
							plotReplicate: d.plotReplicate,
							plotId: d.plotId,
							plotName: d.plotName,
							fieldId: d.fieldId,
							analysisValue: (d.totalAuc / dataNormalization) * getDataNormalizationMultiplier(),
							trialTreatmentId: d.trialTreatmentId,
							assessmentId: aucTrialD[0].analysisId,
							trialId: d.trialId,
							dataNormalizationId: selectedNormalizationOption,
							alphaId: selectedAlphaOption,
							meanComparisonId: selectedMeanComparisonTestOption,
							trialName: d.trialName,
							// TODO(types): AUC rows have no excludeFromAssessment/isOutlier3/isOutlier1_5, so
							// these three are always undefined.
							excludeFromAssessment: d.excludeFromAssessment,
							isOutlier3: d.isOutlier3,
							isOutlier1_5: d.isOutlier1_5,
						};
					},
				);

				if (aucTrialD?.length > 0) {
					//for box and whisker plot
					_.map(aucTreatmentData, (t) => {
						const trialChecked =
							_.find(trialsChecked, (tc) => {
								return tc.trialId === aucTrialD[0].trialId;
							})?.trialChecked ?? true;
						const plotObject: TimecoursePlotRow = {
							trialId: t.trialId,
							trialName: t.trialName,
							region: t.region,
							country: t.country,
							countryCode: t.countryCode,
							city: t.city,
							cooperatorName: t?.cooperatorName,
							trialTreatmentId: i,
							plotName: t.plotName,
							plotReplicate: t.plotReplicate,
							average: (t.totalAuc / dataNormalization) * getDataNormalizationMultiplier(),
							excluded: false,
							//excludeFromAssessment: false,
							// TODO(types): AUC rows have no excludeFromAssessment/isOutlier3/isOutlier1_5/flightDate,
							// so these four are always undefined.
							excludeFromAssessment: t.excludeFromAssessment,
							isOutlier3: t.isOutlier3,
							isOutlier1_5: t.isOutlier1_5,
							trialChecked: trialChecked,
							treatmentChecked:
								_.find(treatmentsChecked, (tc) => {
									return tc.trialTreatmentId === i;
								})?.treatmentChecked ?? true,
							analysisId: t.analysisId,
							analysisTypeId: selectedAnalysisTypeOption,
							flightDate: t.flightDate,
						};

						plotObjects.push(plotObject);
					});

					const siteObject: TimecourseSiteRow = {
						trialId: aucTrialD[0].trialId,
						trialName: aucTrialD[0].trialName,
						region: aucTrialD[0].region,
						country: aucTrialD[0].country,
						countryCode: aucTrialD[0].countryCode,
						city: aucTrialD[0].city,
						cooperatorName: aucTrialD[0].cooperatorName,
						trialTreatmentId: i,
						average: 0,
						standardDeviation: 0,
						excluded: true,
						trialChecked:
							_.find(trialsChecked, (tc) => {
								return tc.trialId === aucTrialD[0].trialId;
							})?.trialChecked ?? true,
						treatmentChecked:
							_.find(treatmentsChecked, (tc) => {
								return tc.trialTreatmentId === i;
							})?.treatmentChecked ?? true,
						analysisId: aucTrialD[0].analysisId,
						analysisTypeId: selectedAnalysisTypeOption,
						// TODO(types): AUC rows have no flightDate, so this is always undefined.
						flightDate: aucTrialD[0].flightDate,
						plots: trialTreatmentPlotData,
						assessmentName: orthoTypeName,
					};
					if (aucTreatmentData.length > 0) {
						const aucTreatmentValues = _.map(aucTreatmentData, (data) => {
							return (data.totalAuc / dataNormalization) * getDataNormalizationMultiplier();
						});
						const average = _.meanBy(aucTreatmentValues);
						siteObject.average = average;

						const standardDeviation = std(aucTreatmentValues) / average;
						siteObject.standardDeviation = standardDeviation;

						siteObject.excluded = aucTreatmentValues.every((item) => item === 0) ? true : false;
					}

					trialData.push(siteObject);
				}
			}
			siteData = siteData.concat(trialData);
		});

		const filteredData = _.filter(
			siteData,
			(sd) =>
				sd.analysisId === selectedOrthoTypeOption &&
				sd.analysisTypeId === selectedAnalysisTypeOption &&
				// parseFloat coerces to a string anyway; String() just satisfies the types
				!isNaN(parseFloat(String(sd.average))),
		);
		if (!_.isEmpty(filteredData)) {
			const filteredPlots = _.filter(
				plotObjects,
				(p) =>
					p.analysisId === selectedOrthoTypeOption &&
					p.analysisTypeId === selectedAnalysisTypeOption &&
					!isNaN(parseFloat(String(p.average))),
			);

			return { filteredSiteSpecificData: filteredData, trialPlotSpecificData: filteredPlots };
		}

		return { filteredSiteSpecificData: null, trialPlotSpecificData: null };
	}, [
		committedUncheckedTreatmentIds,
		filteredAucData,
		getDataNormalizationModifier,
		getDataNormalizationMultiplier,
		orthoTypeOptions,
		treatmentCount,
		selectedAlphaOption,
		selectedAnalysisTypeOption,
		selectedMeanComparisonTestOption,
		selectedNormalizationOption,
		selectedOrthoTypeOption,
		treatmentsChecked,
		trialsChecked,
	]);

	const trialInfo = useMemo(
		() =>
			_.sortBy(
				_.map(
					_.filter(trialData, (td) =>
						_.map(_.uniqBy(filteredSiteSpecificData, "trialId"), "trialId").includes(td.trialInfo.trialId),
					),
					(td) => td.trialInfo,
				),
				["farmCountry", "farmRegion", "farmCity", "trialName", "cooperatorName", "trialId"],
			),
		[trialData, filteredSiteSpecificData],
	);

	/* -------- pipeline: normalized treatment averages ------------------------------------- */

	// The last pipeline stage: site specific rows collapsed to one row per treatment, with the
	// selected normalization applied.
	const compiledData = useMemo(() => {
		if (!dataNormalizationOptions || !selectedNormalizationOption || !filteredSiteSpecificData) return null;
		// The compiled table and its mean comparison are statistics, so they follow the committed
		// selection. The `trialChecked`/`treatmentChecked` row stamps track the live checkboxes instead.
		const isSelectedTrial = (trialId: string) =>
			committed.selectedTrialIds ? committed.selectedTrialIds.includes(trialId) : true;
		// `treatmentChecked` greys table rows, so it tracks the live checkbox (isTickedTreatment), not
		// the committed matrix. Nothing statistical reads it; compiledTukeyRequest gates on `committed`.
		const averageData: TimecourseCompiledRow[] = [];
		const selectedDataNormalizationName = _.find(dataNormalizationOptions, [
			"value",
			selectedNormalizationOption,
		])!.text.toLowerCase();
		for (let i = 1; i <= treatmentCount; ++i) {
			const treatment = _.find(treatmentData, { trialTreatmentId: i });

			let treatmentDataAverages = _.filter(filteredSiteSpecificData, (sd) => {
				return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
			});

			//Filter out trials with no plot data
			treatmentDataAverages = _.filter(treatmentDataAverages, (tda) => {
				return tda?.plots.length > 0;
			});

			if (treatmentDataAverages?.length > 0) {
				const averages = _.map(treatmentDataAverages, "average");
				const average = _.meanBy(averages);

				const standardDeviations = _.map(treatmentDataAverages, "standardDeviation");
				const standardDeviation = _.meanBy(standardDeviations) * 100;

				//Building a list of treatments with missing treatments in the trials. This will help determine which treatment needs an info icon
				const treatmentGroup = _.groupBy(
					_.filter(filteredSiteSpecificData, (fd) => {
						return isSelectedTrial(fd.trialId);
					}),
					"trialTreatmentId",
				);

				const maxNumberOfObjects = _.max(Object.values(treatmentGroup).map((a) => a.length));
				const treatmentsMissingInTrials: number[] = [];
				_.map(treatmentGroup, (tg) => {
					if (
						tg.length != maxNumberOfObjects ||
						_.some(tg, (t) => {
							return t.excluded === true;
						})
					) {
						treatmentsMissingInTrials.push(tg[0].trialTreatmentId);
					}
				});

				// TODO(types): the reads off the `_.filter(...)` results below (trialId, trialName, city,
				// region, country, countryCode) take a property of an array, so they are always
				// undefined; flightDate compares against that undefined trialId, so it never matches
				// either. The intent was presumably the first matching site row. The SiteRowsReadAsRow
				// casts only let those reads type-check.
				const compiledTreatment: TimecourseCompiledRow = {
					trialTreatmentId: i,
					// TODO(types): throws when treatmentData has no treatment with this trialTreatmentId.
					treatmentTypeName: treatment!.treatmentTypeName,
					treatmentName: treatment!.name,
					average: average,
					standardDeviation: standardDeviation,
					treatmentChecked: isTickedTreatment(i),
					trialId: (
						_.filter(filteredSiteSpecificData, (sd) => {
							return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
						}) as SiteRowsReadAsRow
					)?.trialId,
					trialName: (
						_.filter(filteredSiteSpecificData, (sd) => {
							return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
						}) as SiteRowsReadAsRow
					)?.trialName,
					city: (
						_.filter(filteredSiteSpecificData, (sd) => {
							return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
						}) as SiteRowsReadAsRow
					)?.city,
					region: (
						_.filter(filteredSiteSpecificData, (sd) => {
							return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
						}) as SiteRowsReadAsRow
					)?.region,
					country: (
						_.filter(filteredSiteSpecificData, (sd) => {
							return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
						}) as SiteRowsReadAsRow
					)?.country,
					countryCode: (
						_.filter(filteredSiteSpecificData, (sd) => {
							return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
						}) as SiteRowsReadAsRow
					)?.countryCode,
					assessmentData: _.map(treatmentDataAverages, (td) => {
						return {
							plots: td.plots,
						};
					}),
					flightDate: _.find(timecourseData, (tcd) => {
						return (
							tcd.trialTreatmentId === i &&
							tcd.trialId ==
								(
									_.filter(filteredSiteSpecificData, (sd) => {
										return sd.trialTreatmentId === i && isSelectedTrial(sd.trialId);
									}) as SiteRowsReadAsRow
								)?.trialId
						);
						// Never matches (see above), so the result is always undefined
					})?.flightDate as undefined,
					hasMissingTreatments: treatmentsMissingInTrials.includes(i),
					analysisId: filteredSiteSpecificData[0]?.analysisId,
					analysisTypeId: filteredSiteSpecificData[0]?.analysisTypeId,
				};
				averageData.push(compiledTreatment);
			}
		}
		if (selectedDataNormalizationName === "rank order") {
			getRankOrder(averageData);
			_.map(averageData, (ad) => {
				ad.reversed = true;
			});
		}
		return averageData;
	}, [
		dataNormalizationOptions,
		filteredSiteSpecificData,
		treatmentCount,
		selectedNormalizationOption,
		timecourseData,
		treatmentData,
		isTickedTreatment,
		committed.selectedTrialIds,
	]);

	/* -------- display views: pipeline output stamped with the live selection --------------- */

	// What the user sees: the pipeline output above, stamped with the live checkbox state. A tick
	// only dispatches; `pending` moves and these restamp.
	const siteSpecificDataForDisplay = useMemo(
		() =>
			_.map(filteredSiteSpecificData, (ssd) => ({
				...ssd,
				treatmentChecked: isTickedTreatment(ssd.trialTreatmentId),
				trialChecked: isTickedTrial(ssd.trialId),
			})),
		[filteredSiteSpecificData, isTickedTreatment, isTickedTrial],
	);

	const plotSpecificDataForDisplay = useMemo(
		() =>
			_.map(trialPlotSpecificData, (tpsd) => ({
				...tpsd,
				treatmentChecked: isTickedTreatment(tpsd.trialTreatmentId),
				trialChecked: isTickedTrial(tpsd.trialId),
			})),
		[trialPlotSpecificData, isTickedTreatment, isTickedTrial],
	);

	// The line charts and the per-trial panels drop unticked treatments rather than stamping them.
	// `_.filter` builds a fresh treatments array, leaving `filteredTrialData` intact.
	const trialDataForDisplay = useMemo(
		() =>
			_.map(filteredTrialData, (ftd) => ({
				...ftd,
				treatments: _.filter(ftd.treatments, (t) => isTickedTreatment(t.trialTreatmentId)),
			})),
		[filteredTrialData, isTickedTreatment],
	);

	/* -------- statistics: mean comparisons and anova -------------------------------------- */

	const compiledTukeyRequest = useMemo((): CompiledMeanComparisonRequest<TimecourseSitePlot> => {
		if (!compiledData)
			return {
				groups: [],
				hasMultipleTrials: true,
			};
		const allPlotData: TimecourseSitePlot[] = [];
		//closure to check if a treatment is selected
		const isSelectedTreatment = (trialTreatmentId: number) =>
			committed.selectedTreatmentMatrix ? !!committed.selectedTreatmentMatrix[trialTreatmentId] : true;

		for (const cd of compiledData) {
			if (!isSelectedTreatment(cd.trialTreatmentId) || cd.hasMissingTreatments) continue;
			for (const ad of cd.assessmentData) {
				allPlotData.push(..._.filter(ad.plots, (pl) => !!pl.plotId));
			}
		}

		return {
			groups: _.values(_.groupBy(allPlotData, "assessmentId")),
			hasMultipleTrials: _.uniqBy(allPlotData, "trialId").length > 1,
		};
	}, [compiledData, committed.selectedTreatmentMatrix]);

	const {
		data: compiledTukey,
		isFetching: compiledTukeyRunning,
		isError: compiledTukeyFailed,
	} = useQuery({
		queryKey: [
			"protocol compiled mean comparison data",
			moduleNavigation.protocolId,
			userAuth.currentClientId,
			compiledTukeyRequest.groups,
			committed.alphaOptionId,
			committed.meanComparisonOptionId,
		],
		queryFn: async () => {
			const accessToken = await getTokenSilently();

			//The endpoint takes one assessment/planned timing group at a time
			//TODO: Convert to a single call
			return Promise.all(
				_.map(compiledTukeyRequest.groups, (plotData) =>
					getProtocolTukeyDataAsync(
						// TODO(types): these plots carry `plotReplicate: number | null`, a possibly undefined
						// plotId and nullable dataNormalizationId/alphaId/meanComparisonId, which the request
						// row requires to be set. Sent as they are.
						plotData as ProtocolMeanComparisonRequestRow[],
						// `enabled` gates the query on all three
						committed.alphaOptionId!,
						committed.meanComparisonOptionId!,
						userAuth.currentClientId!,
						accessToken,
					),
				),
			);
		},
		select: (responses): CompiledMeanComparisonResult<TimecourseAnovaData> => {
			return {
				// TODO(types): `data` is null when the server rejects the input (e.g. all values zero),
				// which throws here and fails the query.
				letters: buildMeanComparisonLetters(_.flatMap(responses, (r) => r.data!.protocolData ?? [])),
				trialLetters: buildMeanComparisonLetters(_.flatMap(responses, (r) => r.data!.trialData ?? [])),
				anovaData: buildAnovaData(_.last(responses)?.data?.protocolData?.[0], compiledData?.[0]?.analysisTypeId),
			};
		},
		enabled:
			compiledTukeyRequest.groups.length > 0 &&
			compiledTukeyRequest.hasMultipleTrials &&
			!!committed.alphaOptionId &&
			!!committed.meanComparisonOptionId &&
			!!userAuth.currentClientId,
	});

	const compiledTukeyData = compiledTukey?.letters ?? {};
	const compiledTrialTukeyData = compiledTukey?.trialLetters ?? {};
	const anovaData = compiledTukey?.anovaData ?? null;

	//A protocol with one trial has plots to compare but nothing to compare them against.
	const oneTrialDatasetError = compiledTukeyRequest.groups.length > 0 && !compiledTukeyRequest.hasMultipleTrials;
	const multiTrialDatasetError = compiledTukeyFailed;
	const tukeyRunning = singleTrialTukeyRunning || compiledTukeyRunning;

	/* -------- effects: page knowledge published into the context -------------------------- */

	// These effects publish facts that only this page has fetched into the context.

	// The top bar offers the GDU axis only when every trial has a plant date and the crop has a GDU formula.
	useEffect(() => {
		changeDisplaySettings({
			hasGduFormula: !!gduByDay && areaUnderTheCurve.checkForGDUFormula(timecourseData?.[0]?.cropName),
		});
	}, [gduByDay, timecourseData, changeDisplaySettings]);

	// The top bar owns the Trial Filters dropdown; applying it happens here because matching a filter
	// needs each trial's quality/status/response ids.
	useEffect(() => {
		if (trialData && !!display.trialFilterId && display.trialFilterId !== CUSTOM_TRIAL_FILTER) {
			const passingTrialIds = _.map(
				_.filter(
					trialData,
					({ trialInfo }) =>
						!display.trialFilters.status.includes(trialInfo.trialExclusionStatusId) &&
						!display.trialFilters.response.includes(trialInfo.trialSiteResponseId) &&
						!display.trialFilters.quality.includes(trialInfo.trialDataQualityId),
				),
				(td) => td.trialInfo.trialId,
			);
			if (committed.selectedTrialIds === null) {
				// Bypasses committed vs pending: the default filter is not a user edit
				initializeSettings({ selectedTrialIds: passingTrialIds });
			} else if (_.xor(pending.selectedTrialIds, passingTrialIds).length > 0) {
				// Picking a filter stages like the checkboxes do. Guarded on a real difference: this effect
				// also runs after a commit, and re-dispatching the committed ids would make the page dirty again.
				changeSettings({ selectedTrialIds: passingTrialIds });
			}
		}
	}, [
		display.trialFilterId,
		display.trialFilters,
		trialData,
		pending.selectedTrialIds,
		committed.selectedTrialIds,
		changeSettings,
		initializeSettings,
	]);

	useEffect(() => {
		if (filteredTrialData?.length > 0) {
			const hasAllPlantDates = _.every(filteredTrialData, (td) => {
				return td?.trialInfo?.plantDate;
			});

			changeDisplaySettings({ allTrialsHavePlantDate: hasAllPlantDates });
		}
	}, [filteredTrialData, changeDisplaySettings]);

	/* -------- treatment / trial selection handlers ---------------------------------------- */

	// Both handlers take one id or a list and dispatch once. Each call reads `pending` from this
	// render, so calling them in a loop would leave only the last change standing.
	function updateCheckedTreatment(value: boolean, trialTreatmentIdOrIds: number | number[]) {
		const ids = _.castArray(trialTreatmentIdOrIds);
		const matrix = [...(pending.selectedTreatmentMatrix ?? buildTreatmentMatrix(treatmentData))];
		for (const id of ids) {
			matrix[id] = value;
		}
		changeSettings({ selectedTreatmentMatrix: matrix });
	}

	function updateTrialsChecked(value: boolean, trialIdOrIds: string | string[], silent = false) {
		if (!silent) changeDisplaySettings({ trialFilterId: CUSTOM_TRIAL_FILTER });
		const ids = _.castArray(trialIdOrIds);
		const current = pending.selectedTrialIds ?? trialIds;
		changeSettings({ selectedTrialIds: value ? _.union(current, ids) : _.difference(current, ids) });
	}

	/* -------- render ---------------------------------------------------------------------- */

	const getLineChartData = useCallback(
		(trialsD: FlattenedTrialMetadata) => {
			const uncheckedList: number[] = [];
			_.filter(treatmentsChecked, (utc) => {
				if (!utc.treatmentChecked) {
					uncheckedList.push(utc.trialTreatmentId);
				}
			});

			let shouldContainHarvestFlight = !!trialsD.trialInfo.harvestDate;

			//This helps us filter out harvest flight from the line charts depending on the "End" date set for DAP
			if (trialsD.trialInfo.harvestDate && timecourseEnd !== 0 && !useGdu) {
				const firstFlightDate =
					timecourseStart === 0
						? (trialsD.trialInfo.plantDate ??
							_.minBy(
								_.filter(timecourseData, (x) => {
									return x.trialId === trialsD.trialInfo.trialId;
								}),
								"flightDate",
							)?.flightDate)
						: null;
				const endFlightDate = moment(firstFlightDate).add(Number(timecourseEnd), "d");
				shouldContainHarvestFlight = moment(trialsD.trialInfo.harvestDate) <= endFlightDate;
			}

			//This helps us filter out harvest flight from the line charts depending on the "End" date set for GDU
			if (trialsD.trialInfo.harvestDate && timecourseEnd !== 0 && useGdu) {
				let plantAndHarvestFlightValues: AucInputRow[] | null = _.cloneDeep(timecourseData);
				const harvestTreatments = areaUnderTheCurve.addEmptyFlightForPlantOrHarvestDate(
					trialsD.trialInfo.harvestDate,
					_.filter(timecourseData, (tcd) => {
						return (
							tcd.trialId == trialsD.trialInfo.trialId &&
							tcd.quantifiedRegionTypeId === selectedQuantifiedRegionTypeOption
						);
					}),
					trialsD.treatments,
					orthoTypeOptions,
					true,
					false,
					userAuth.currentClientId,
				);
				if (harvestTreatments) {
					// Only charted trials get here, and there are none while timecourseData is null
					plantAndHarvestFlightValues = plantAndHarvestFlightValues!.concat(harvestTreatments);
				}

				const trialsGDUsByDay = _.find(gduByDay, (gdu) => {
					return gdu.trialId === trialsD.trialInfo.trialId;
				});

				const flightGDUs = areaUnderTheCurve.calculateCumulativeGdu(
					plantAndHarvestFlightValues,
					// TODO(types): throws when gduByDay is null or has no entry for the trial.
					trialsGDUsByDay!.data,
				);
				// TODO(types): throws when there are no cumulative GDUs (no GDU data for the trial).
				const maxGDUValue = _.maxBy(flightGDUs, (fd) => Number(fd.gdu))!.gdu;

				// TODO(types): compares the timecourseData *array* to a number. The array is coerced via
				// its string form ("[object Object],..." → NaN), so this is false unless the array is
				// empty. Probably meant the harvest date's GDU.
				shouldContainHarvestFlight = (timecourseData as unknown as number) <= Number(maxGDUValue);
			}

			const lineChartData = areaUnderTheCurve.setupTrialAverages(
				_.filter(timecourseData, (tcd) => {
					return (
						tcd.trialId == trialsD.trialInfo.trialId &&
						tcd.analysisId === selectedOrthoTypeOption &&
						tcd.quantifiedRegionTypeId === selectedQuantifiedRegionTypeOption &&
						tcd.analysisTypeId === selectedAnalysisTypeOption &&
						!_.includes(uncheckedList, tcd.trialTreatmentId)
					);
				}),
				selectedOrthoTypeOption,
				0,
				trialsD,
				Number(initialTimecourseStart) === 0, //using initial so that the line charts dont get updated until the update analytics button is clicked
				shouldContainHarvestFlight,
				removeDamagedOrExcluded,
				removeOutliers,
				outlierType,
			);
			return lineChartData;
		},
		[
			gduByDay,
			initialTimecourseStart,
			orthoTypeOptions,
			outlierType,
			removeDamagedOrExcluded,
			removeOutliers,
			selectedAnalysisTypeOption,
			selectedOrthoTypeOption,
			selectedQuantifiedRegionTypeOption,
			timecourseData,
			timecourseEnd,
			timecourseStart,
			treatmentsChecked,
			useGdu,
			userAuth.currentClientId,
		],
	);

	// Mean comparisons need at least two treatments and two trials to compare
	const handleCommit = () => {
		if (
			_.filter(treatmentsChecked, (tc) => tc.treatmentChecked).length < 2 ||
			_.filter(trialsChecked, (tc) => tc.trialChecked).length < 2
		) {
			toast.error("At least two treatments and two trials need to be selected");
			return;
		}

		commitSettings();
	};

	const lineChartData = useMemo(() => {
		return _.reduce<FlattenedTrialMetadata, Record<string, TrialAverageRow[]>>(
			trialDataForDisplay,
			(acc, curr) => {
				return { ...acc, [curr.trialInfo.trialId]: getLineChartData(curr) };
			},
			{},
		);
	}, [getLineChartData, trialDataForDisplay]);

	return (
		<Segment basic style={{ padding: 0 }}>
			<ProtocolAnalysisTopBar
				mode="timecourse"
				dashboardOptions={dashboardOptions}
				standCountAnalyses={standCountAnalyses}
				treatments={treatmentData}
				analysisResults={analysisOptionRows}
				timeSeriesPresets={timeSeriesPresets}
				loading={loading}
				disabled={tukeyRunning || metadataLoading}
				onCommit={handleCommit}
			/>
			{timecourseData?.length === 0 ? (
				<Segment style={{ width: "100%" }}>
					<p>{"We could not find data for this protocol."}</p>
				</Segment>
			) : (
				<Form>
					<Grid>
						{loading ? null : (
							<Grid.Row style={{ paddingTop: "1em" }}>
								<Grid.Column width="6" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
								<Grid.Column width="4" textAlign="center">
									<h2>Compiled Protocol Data</h2>
								</Grid.Column>
								<Grid.Column width="6" verticalAlign="middle">
									<hr style={{ width: "100%" }} />
								</Grid.Column>
							</Grid.Row>
						)}

						{filteredSiteSpecificData != null &&
						filteredSiteSpecificData?.length > 0 &&
						compiledData != null &&
						compiledData.length > 0 &&
						trialPlotSpecificData != null &&
						trialPlotSpecificData.length > 0 ? (
							<Grid.Row>
								<Grid.Column className="aucTableContainer" width="8">
									<ProtocolDataNormalizationTable
										compiledData={compiledData}
										tukeyData={compiledTukeyData}
										disableCheckBoxes={tukeyRunning}
										selectedAssessmentOption={selectedOrthoTypeOption}
										dataNormalization={
											_.find(dataNormalizationOptions, (dno) => {
												return dno.value === selectedNormalizationOption;
											})!.text
										}
										updateCheckedTreatment={updateCheckedTreatment}
										treatmentsChecked={treatmentsChecked}
										siteSpecificData={siteSpecificDataForDisplay}
										updateTrialsChecked={updateTrialsChecked}
										trialsChecked={trialsChecked}
										tukeySiteData={compiledTrialTukeyData}
										hideLetters={isLettersOutdated}
										hideUncheckedTrialData={hideUncheckedTrialData}
										isForProtocolTimecoursePage={true}
										oneTrialDatasetError={oneTrialDatasetError}
										multiTrialDatasetError={multiTrialDatasetError}
										collapseView={display.collapseView}
									/>
								</Grid.Column>
								<Grid.Column width="8">
									<Grid.Row>
										{filteredSiteSpecificData ? (
											<ProtocolBoxWhiskerChart
												// String() matches the key a null id coerces to
												tukeyData={compiledTukeyData[String(selectedOrthoTypeOption)]}
												tukeyRunning={tukeyRunning}
												data={_.filter<TimecourseSiteRow | TimecoursePlotRow>(
													showAveragesChecked
														? !_.some(treatmentsChecked, (tc) => tc.treatmentChecked)
															? siteSpecificDataForDisplay
															: _.filter(siteSpecificDataForDisplay, (ssd) => {
																	return ssd.treatmentChecked && ssd.trialChecked && _.some(ssd.plots, (p) => p.plotId);
																})
														: !_.some(treatmentsChecked, (tc) => tc.treatmentChecked)
															? plotSpecificDataForDisplay
															: _.filter(plotSpecificDataForDisplay, (tps) => {
																	return !!(tps.treatmentChecked && tps.trialChecked && tps.plotName);
																}),
												)}
												axisYTitle={`AUC ${selectedAssessmentName} (${
													_.find(dataNormalizationOptions, ["key", selectedNormalizationOption])?.text
												})`}
												assessmentName={selectedAssessmentName}
												chartHeight={500}
												allTreatmentData={_.filter<TimecourseSiteRow | TimecoursePlotRow>(
													showAveragesChecked ? siteSpecificDataForDisplay : plotSpecificDataForDisplay,
												)}
												initialGroupTrialDataCheckedCount={initialGroupTrialDataCheckedCount}
												allTreatmentsUnchecked={!_.some(treatmentsChecked, (tc) => tc.treatmentChecked)}
												hideUncheckedTrialData={hideUncheckedTrialData}
												isCompiled={showAveragesChecked ? "averages" : "allReps"}
												disableColors={disableColors}
											/>
										) : (
											<>There is no data to display for the DAP range</>
										)}
									</Grid.Row>
									<Grid.Row>
										<Grid>
											<Grid.Column width="4">
												<Form.Radio
													checked={showAveragesChecked}
													label="Trial Averages"
													disabled={loading || tukeyRunning}
													onChange={() => {
														changeDisplaySettings({ showAverages: true });
													}}
												/>
												<Form.Radio
													checked={!showAveragesChecked}
													label="All Replicates"
													disabled={loading || tukeyRunning}
													onChange={() => {
														changeDisplaySettings({ showAverages: false });
													}}
												/>
												<Form.Checkbox
													checked={disableColors}
													onChange={() => changeDisplaySettings({ disableColors: !disableColors })}
													label="Disable Graph Colors"
													toggle
												/>
											</Grid.Column>
											<Grid.Column className="aucTableContainer" width="12">
												<AnovaTable
													anovaData={anovaData}
													hideData={tukeyRunning || isLettersOutdated}
													wasThereAnError={multiTrialDatasetError || oneTrialDatasetError}
												/>
											</Grid.Column>
										</Grid>
									</Grid.Row>
								</Grid.Column>
							</Grid.Row>
						) : (
							<Loader active inline="centered" />
						)}
						{filteredSiteSpecificData && trialInfo && filteredSiteSpecificData?.length > 0 && (
							<>
								<Grid.Row>
									<Grid.Column width="7" verticalAlign="middle">
										<hr style={{ width: "100%" }} />
									</Grid.Column>
									<Grid.Column width="2" textAlign="center">
										<h2>Trial Data</h2>
									</Grid.Column>
									<Grid.Column width="7" verticalAlign="middle">
										<hr style={{ width: "100%" }} />
									</Grid.Column>
								</Grid.Row>
								<Grid.Row>
									<Grid.Column width="16" textAlign="center">
										<h4>{`AUC ${selectedAssessmentName} (${
											_.find(dataNormalizationOptions, ["key", selectedNormalizationOption])?.text
										})`}</h4>
									</Grid.Column>
									<Grid.Column width="8" style={{ display: "none" }}>
										<Form.Group inline>
											<label>Perform Mean Comparisons as: </label>
											<Form.Radio
												checked={showTwoWayAnova}
												label={"Single Multi-Location Data Set"}
												disabled={loading || tukeyRunning}
												onChange={() => changeDisplaySettings({ anovaType: "two-way" })}
											></Form.Radio>
											<Form.Radio
												checked={!showTwoWayAnova}
												label={"Seperate, Isolated Data Sets"}
												disabled={loading || tukeyRunning}
												onChange={() => changeDisplaySettings({ anovaType: "one-way" })}
											></Form.Radio>
										</Form.Group>
									</Grid.Column>
								</Grid.Row>
							</>
						)}
						{filteredSiteSpecificData && trialInfo && filteredSiteSpecificData?.length > 0 ? (
							<>
								<div className={sharedStyles.trialTableWrapper}>
									<Grid.Row className="aucTableContainer" style={{ padding: 0 }}>
										<MetaTagsTable trialInfo={trialInfo} collapseView={display.collapseView} />
									</Grid.Row>
									<Grid.Row className="aucTableContainer" style={{ padding: 0 }}>
										<ProtocolSiteSpecificTable
											siteSpecificData={siteSpecificDataForDisplay}
											tukeyData={
												showTwoWayAnova
													? compiledTrialTukeyData[String(selectedOrthoTypeOption)]
													: _.flatten(
															_.map(singleTrialAnovaValues, (data) => {
																return data.letteringPairs;
															}),
														)
											}
											tukeyDataLoading={tukeyRunning}
											disableCheckBoxes={tukeyRunning}
											updateCheckedTrial={updateTrialsChecked}
											appendedPercent={_.find(dataNormalizationOptions, [
												"value",
												selectedNormalizationOption,
											])!.text.includes("%")}
											hideMeanComparisonResults={isLettersOutdated}
											hideCheckAll={false}
											collapseView={display.collapseView}
										/>
									</Grid.Row>
								</div>

								<Grid.Row centered>
									<Grid.Column>
										<Grid>
											{timecourseData != null &&
												timecourseData.length > 0 &&
												trialIds &&
												filteredTrialData != null &&
												filteredTrialData.length > 0 &&
												trialsChecked != null && (
													<>
														<Grid.Row>
															<Grid.Column width="6" verticalAlign="middle">
																<hr style={{ width: "100%" }} />
															</Grid.Column>
															<Grid.Column width="4" textAlign="center">
																<h2>Trial Growth Curves</h2>
															</Grid.Column>
															<Grid.Column width="6" verticalAlign="middle">
																<hr style={{ width: "100%" }} />
															</Grid.Column>
														</Grid.Row>
														{_.map(trialDataForDisplay, (trialsD) => {
															return (
																<Grid.Row key={trialsD.trialInfo.trialId}>
																	<Grid.Column className="aucTableContainer" width="3">
																		<ProtocolTimecourseSiteSpecificTable
																			trialId={trialsD.trialInfo.trialId}
																			trialName={trialsD.trialInfo.trialName}
																			cooperatorName={trialsD.trialInfo.cooperatorName}
																			farmCity={trialsD.trialInfo.farmCity}
																			farmRegion={trialsD.trialInfo.farmRegion}
																			farmCountryCode={trialsD.trialInfo.farmCountryCode}
																			clientId={userAuth.currentClientId}
																			hasExclusions={trialsD.trialInfo.hasExclusions}
																			hasFlaggings={trialsD.trialInfo.hasFlaggings}
																			hasPartialExclusions={trialsD.trialInfo.hasPartialExclusions}
																			hasPartialFlaggings={trialsD.trialInfo.hasPartialFlaggings}
																			toggleMetaFactorModal={toggleMetaFactorModal}
																		/>
																	</Grid.Column>
																	<Grid.Column width="13">
																		<Grid>
																			<Grid.Column width="8" style={{ paddingTop: 0 }}>
																				<LineChart
																					data={lineChartData[trialsD.trialInfo.trialId]}
																					analysisName={
																						selectedAssessmentName?.includes("Uniformity")
																							? selectedAssessmentName
																							: _.find(orthoTypeOptions, (oto) => {
																									return oto.key === selectedOrthoTypeOption;
																								})?.text
																					}
																					gduByDay={
																						gduByDay
																							? _.filter(gduByDay, (gduList) => {
																									return gduList.trialId === trialsD.trialInfo.trialId;
																								})[0]?.data
																							: null
																					}
																					useGDU={useGdu}
																					hideLineCharts={hideLineCharts}
																				/>
																			</Grid.Column>

																			<Grid.Column width="8" style={{ paddingTop: 0 }}>
																				{plotSpecificDataForDisplay?.length > 0 ? (
																					<ProtocolBoxWhiskerChart
																						data={_.filter(plotSpecificDataForDisplay, (ssd) => {
																							return ssd.trialId === trialsD.trialInfo.trialId;
																						})}
																						tukeyData={
																							showTwoWayAnova
																								? _.filter(
																										compiledTrialTukeyData[String(selectedOrthoTypeOption)],
																										(data) => {
																											return data.trialId === trialsD.trialInfo.trialId;
																										},
																									)
																								: _.find(singleTrialAnovaValues, (data) => {
																										return data.trialId === trialsD.trialInfo.trialId;
																									})?.letteringPairs
																						}
																						chartHeight={400}
																						assessmentName={selectedAssessmentName}
																						trialName={trialsD.trialInfo.trialName}
																						tukeyRunning={tukeyRunning}
																						hideMeanComparisonResults={isLettersOutdated}
																						initialGroupTrialDataCheckedCount={initialGroupTrialDataCheckedCount}
																						disableColors={disableColors}
																					/>
																				) : null}
																			</Grid.Column>
																		</Grid>
																	</Grid.Column>
																</Grid.Row>
															);
														})}
													</>
												)}
										</Grid>
									</Grid.Column>
								</Grid.Row>
							</>
						) : null}
					</Grid>
				</Form>
			)}
		</Segment>
	);
};

export default ProtocolTimecourse;
